import React from 'react';
import {List, Map} from 'immutable';
import noop from 'lodash/noop';
import StatItem from 'components/dashboard/StatItem';
import ExecTrendChartContainer from 'components/pure/metrics/ExecTrendChartContainer';
import ExecDashboardActions from 'actions/ExecDashboardActions';
import fetchMissingUsers from 'utils/fetchMissingUsers';
import BasedOnDatesLink from 'components/dashboard/common/BasedOnDatesLink';
import TopicRollupTeamDrillDown from 'components/dashboard/rollups/topic/TopicRollupTeamDrillDown';
import Loading from 'components/pure/utils/Loading';
import buildUrl from 'components/dashboard/buildUrl';
import {browserHistory} from 'react-router';
import {fetchTeamStats} from 'components/dashboard/rollups/topic/common';
import {maxByReplacingNaN, sortByReplacingNaN} from 'utils/NaN';
import './TopicRollupSummaryDrillDown.styl';
import DownIconButton from 'components/utils/DownIconButton';
import {HierarchyType} from 'sources/dashboard/DashMetricsSource';
import {callDrillDown} from 'components/dashboard/rollups/topic/CallDrillDown';

/**
 * Drill down into a department, displaying stats for its teams.
 */
export default class TopicRollupSummaryDrillDown extends React.PureComponent {
  constructor(props) {
    super(props);
    this.openTeamDrillDown = this.openTeamDrillDown.bind(this);
    this.closeTeamDrillDown = this.closeTeamDrillDown.bind(this);
    this.state = {
      teamDrillDownOpen: false,
      teamDrillDownReady: false,
      teamDrillDownTeamId: undefined,
      teamDrillDownUsers: List(),
      teamDrillDownConversations: Map(),
    };
  }

  teamDashboardLink(team, stat) {
    // adminId/coachId come from dashboard. See StatsPage2 for the ultimate source.
    const {adminId, coachId, triggerId} = this.props;
    const pathname = buildUrl({adminId, managerId: team.managerId, coachId}) + '/topics';
    return (
      <div className="team-link">
        <DownIconButton
          onClick={() =>
            browserHistory.push({
              pathname,
              state: {
                selectedTeam: team,
              },
              query: {
                triggerId,
              },
            })
          }
        />
        <span className="generic-link pointer" onClick={() => this.openTeamDrillDown(stat)}>
          {team.name}
        </span>
      </div>
    );
  }

  openTeamDrillDown(stat) {
    const {teamDrillDownTeamId} = this.state;
    // Avoid fetching again if we haven't changed teams.
    const callback = teamDrillDownTeamId !== stat.id ? () => this.fetchTeamData(stat.id) : noop;
    this.setState(
      {
        teamDrillDownOpen: true,
      },
      callback
    );
  }

  closeTeamDrillDown() {
    this.setState({
      teamDrillDownOpen: false,
    });
  }

  fetchTeamData(teamId) {
    const {endDate, startDate, subtabConfig, subtabId, triggerId, triggerIdToName} = this.props;
    fetchTeamStats({
      startDate,
      endDate,
      triggerId,
      teamId,
      triggerIdToName,
      includePercentages: subtabConfig.isPercent,
    }).then(
      ([teamDrillDownStats, teamDrillDownUsers]) => {
        this.setState({
          teamDrillDownUsers,
          teamDrillDownStats: teamDrillDownStats.get(subtabId, List()),
          teamDrillDownReady: true,
          teamDrillDownTeamId: teamId,
        });
      },
      e => {
        console.error(e);
        this.setState({
          teamDrillDownError: true,
        });
      }
    );
  }

  render() {
    const {
      chartLabelFn,
      color,
      endDate,
      headerStartDate,
      headerEndDate,
      loading,
      location,
      stats,
      startDate,
      subtabConfig,
      teams,
      triggerId,
      triggerNameFromStat,
    } = this.props;

    if (loading) {
      return <Loading />;
    }

    const max = maxByReplacingNaN(stats.map(stat => stat.value));

    const getLinkAndName = stat => {
      const team = teams
        .valueSeq()
        .filter(team => team.id === stat.id)
        .get(0);
      return team ? [this.teamDashboardLink(team, stat), team.name] : [null, null];
    };

    const fetchTrendData = () => {
      const {subtabConfig} = this.props;

      const [teamId, ...additionalTeamIds] = teams.map(team => team.id);

      let trendPromise;

      if (subtabConfig.isPercent) {
        trendPromise = ExecDashboardActions.getTopicTrendPercentData({
          teamId,
          additionalTeamIds,
          startDate,
          endDate,
          context: triggerId,
        });
      } else {
        trendPromise = ExecDashboardActions.getTrendData({
          type: 'trigger',
          teamId,
          additionalTeamIds,
          startDate,
          endDate,
          context: triggerId,
        });
      }
      trendPromise.then(res => fetchMissingUsers(Object.keys(res)));
    };

    const {teamDrillDownError, teamDrillDownOpen} = this.state;
    if (teamDrillDownError) {
      return <div>Problem loading the data.</div>;
    }

    return teamDrillDownOpen ? (
      <TopicRollupTeamDrillDown
        chartLabelFn={chartLabelFn}
        color={color}
        endDate={endDate}
        error={this.state.teamDrillDownError}
        formatValue={subtabConfig.formatValue}
        goBack={this.closeTeamDrillDown}
        goBackText="< back to team view"
        headerStartDate={headerStartDate}
        headerEndDate={headerEndDate}
        loading={!this.state.teamDrillDownReady}
        location={location}
        stats={this.state.teamDrillDownStats}
        startDate={startDate}
        subtabConfig={subtabConfig}
        triggerId={triggerId}
        triggerNameFromStat={triggerNameFromStat}
        users={this.state.teamDrillDownUsers}
        isPercent={subtabConfig.isPercent}
      />
    ) : (
      <div className="topic-popup-drill-down rep-stat-tab-content">
        <div className="description">{subtabConfig.drillDownDescriptionFn(triggerId)}</div>
        <BasedOnDatesLink startDate={headerStartDate} endDate={headerEndDate} location={location} />
        <div className="bar-container">
          {sortByReplacingNaN(stats, stat => stat.value)
            .reverse()
            .map(stat => (
              <StatItem
                key={stat.id}
                chartLabelFn={chartLabelFn}
                color={color}
                fetchTrendData={fetchTrendData}
                formatValue={subtabConfig.formatValue}
                getLinkAndName={getLinkAndName}
                max={max}
                onOpenDrillDown={callDrillDown({
                  startDate,
                  endDate,
                  hierarchy: HierarchyType.team,
                })}
                stat={stat}
                title={triggerNameFromStat(stat)}
                trendComponent={ExecTrendChartContainer}
              />
            ))}
        </div>
      </div>
    );
  }
}
